import React, { useState } from 'react';
import { Card, Button, DatePicker, Select, Space, Alert, Radio, message } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { HealthDB } from '@/services/db';
import { exportToExcel } from '@/services/export';

const { RangePicker } = DatePicker;

const Export: React.FC = () => {
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [dataType, setDataType] = useState<'all' | 'bp' | 'bs'>('all');
  const [exportFormat, setExportFormat] = useState<'excel' | 'pdf'>('excel');
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (!dateRange) {
      message.warning('请选择日期范围');
      return;
    }

    setLoading(true);
    try {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');

      let data: any[] = [];

      if (dataType === 'all' || dataType === 'bp') {
        const bpData = await HealthDB.getBloodPressureRecordsByDateRange(startDate, endDate);
        data = [...data, ...bpData.map(r => ({
          日期: r.date,
          类型: '血压',
          收缩压: r.systolic,
          舒张压: r.diastolic,
          心率: r.heartRate || '-',
          测量时间: r.measureTime === 'morning' ? '早上' : r.measureTime === 'evening' ? '晚上' : '-'
        }))];
      }

      if (dataType === 'all' || dataType === 'bs') {
        const bsData = await HealthDB.getBloodSugarRecordsByDateRange(startDate, endDate);
        data = [...data, ...bsData.map(r => ({
          日期: r.date,
          类型: '血糖',
          数值: r.value,
          测量类型: r.type === 'fasting' ? '空腹' : 
                   r.type === 'post_meal_1h' ? '餐后1h' :
                   r.type === 'post_meal_2h' ? '餐后2h' : '随机'
        }))];
      }

      // Sort by date
      data.sort((a, b) => new Date(b.日期).getTime() - new Date(a.日期).getTime());

      if (data.length === 0) {
        message.warning('选定范围内没有数据');
        return;
      }

      const filename = `健康数据_${startDate}_${endDate}`;

      if (exportFormat === 'excel') {
        await exportToExcel(data, filename);
        message.success('Excel导出成功');
      } else {
        // PDF export would be implemented here
        message.info('PDF导出功能开发中，请使用Excel导出');
      }
    } catch (error) {
      console.error('Export error:', error);
      message.error('导出失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ marginBottom: 24 }}>
        <DownloadOutlined style={{ marginRight: 8 }} />
        数据导出
      </h1>

      <Alert
        message="导出数据供医生查看"
        description="选择时间范围和需要导出的数据类型，生成Excel或PDF文件"
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card title="导出设置" style={{ maxWidth: 600 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>日期范围：</label>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>数据类型：</label>
            <Select
              value={dataType}
              onChange={setDataType}
              style={{ width: '100%' }}
              options={[
                { label: '全部数据', value: 'all' },
                { label: '仅血压', value: 'bp' },
                { label: '仅血糖', value: 'bs' }
              ]}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: 8 }}>导出格式：</label>
            <Radio.Group value={exportFormat} onChange={e => setExportFormat(e.target.value)}>
              <Radio.Button value="excel">
                <FileExcelOutlined /> Excel
              </Radio.Button>
              <Radio.Button value="pdf">
                <FilePdfOutlined /> PDF
              </Radio.Button>
            </Radio.Group>
          </div>

          <Button
            type="primary"
            size="large"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            loading={loading}
            block
          >
            导出数据
          </Button>
        </Space>
      </Card>
    </div>
  );
};

export default Export;
